import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { addYears, format } from 'date-fns';
import messages from 'messages';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Dot,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Box, Grid } from '@mui/material';
import { useT } from '@transifex/react';
import { theme, Typography, useRichTextEditor } from '@raisin/components-lib';
import { localizeMoney, localizePercent } from '@raisin/cp-utils';
import { ChartBodyProps, Portfolio } from '../types';
import { checkInputsIsValid } from '../utils';
import * as S from './styles';
import { formatTickValue, generatePerformace } from './utils';

const CustomDot: React.FC<{ cx: number; cy: number }> = ({ cx, cy }) => (
  <g>
    {/* Larger circle with blue 60 and add 10% opacity */}
    <circle cx={cx} cy={cy} r={10} fill={`${theme.palette?.['blue60'].main}1A`} />
    {/* Smaller dot */}
    <Dot
      cx={cx}
      cy={cy}
      r={6}
      fill={theme.palette?.['blue60'].main}
      stroke={theme.palette?.['white'].main}
      strokeWidth={1.5}
    />
  </g>
);

const ChartBody: React.FC<ChartBodyProps> = ({
  startAmount,
  monthlyAmount,
  portfolio,
  duration,
  openDetailsModal,
  hasMoneyMarket,
}) => {
  const t = useT();
  const intl = useIntl();
  const { locale, formatNumber } = intl;
  const [portfolioData, setPortfolioData] = useState<Portfolio[]>([]);
  const [userPayment, setUserPayment] = useState(0);
  const [projectionData, setProjectionData] = useState<Array<any>>([]);
  const [differenceYearsNumber, setDifferenceYearsNumber] = React.useState<number>(0);
  const [maxChartMedianValue, setChartMaxMedianValue] = useState(0);
  const costPerYear = [0.0057, 0.0058, 0.0059, 0.0061, 0.0035];

  const prognoseChartRiskParagraph = useRichTextEditor(t(messages.DWMPrognoseChartRiskParagraph), {
    a: ({ children }) => (
      <a href="https://www.weltsparen.de/investieren/risikohinweise">{children}</a>
    ),
  });

  useEffect(() => {
    fetch(
      'https://online-acquisition-obs-assets.s3.eu-central-1.amazonaws.com/investment_portfolios.json',
    )
      .then((res) => res.json())
      .then((response) => {
        if (!response.error) {
          setProjectionData(response);
        }
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.log(err);
      });
  }, []);

  useEffect(() => {
    if (projectionData.length && checkInputsIsValid(monthlyAmount, startAmount, hasMoneyMarket)) {
      const { calculatedPerformance, differenceYears, allUserPayment, maxMedianValue } =
        generatePerformace(
          projectionData[portfolio].projection_data,
          startAmount,
          monthlyAmount,
          duration,
        );

      setPortfolioData(calculatedPerformance);
      setDifferenceYearsNumber(differenceYears);
      setUserPayment(allUserPayment);
      setChartMaxMedianValue(maxMedianValue);
    }
    return () => {};
  }, [projectionData, startAmount, monthlyAmount, duration, portfolio]);

  const formatNumbers = useCallback(
    (value) =>
      localizeMoney({ currency: 'EUR', value: value || 0 }, locale, {
        maximumFractionDigits: 0,
        currencyDisplay: 'symbol',
      }),
    [localizeMoney],
  );

  const renderCustomizedLegend = useCallback(
    () => (
      <>
        <S.Title variant="body1" mb={8}>
          {t(messages.DWMPrognoseChartFooter)}{' '}
          <S.Link onClick={openDetailsModal} mode="secondaryText">
            {t(messages.DWMPrognoseChartOpenModalCTA)}
          </S.Link>
        </S.Title>
        <S.LengendBox p={6}>
          <Grid container columnSpacing={6}>
            <Grid item xs={12} lg={6}>
              <S.LegendElement>
                <S.LegendTitle variant="body1">
                  {t(messages.DWMPrognoseChartLegendTotalContributes)}
                </S.LegendTitle>
                <S.LegendValue>{formatNumbers(userPayment)}</S.LegendValue>
              </S.LegendElement>
              <S.LegendElement $noMDBorder>
                <S.LegendTitle variant="body1">
                  {t(messages.DWMPrognoseChartLegendCostsPerYear)}
                </S.LegendTitle>
                <S.LegendValue>{localizePercent(costPerYear[portfolio])}</S.LegendValue>
              </S.LegendElement>
            </Grid>
            <Grid item xs={12} lg={6}>
              <S.LegendElement pt={{ xs: 4, lg: 0 }}>
                <S.LegendTitle variant="body1">
                  {t(messages.DWMPrognoseChartLegendTotalReturn)}
                </S.LegendTitle>
                <S.LegendValue>{formatNumbers(maxChartMedianValue - userPayment)}</S.LegendValue>
              </S.LegendElement>
              <S.LegendElement $noBorder>
                <S.LegendTitle variant="body1">
                  {t(messages.DWMPrognoseChartLegendFinalCapital)}
                </S.LegendTitle>
                <S.LegendValue>{formatNumbers(maxChartMedianValue)}</S.LegendValue>
              </S.LegendElement>
            </Grid>
          </Grid>
        </S.LengendBox>
      </>
    ),
    [
      costPerYear,
      duration,
      userPayment,
      maxChartMedianValue,
      portfolio,
      formatNumbers,
      openDetailsModal,
    ],
  );

  const CustomTooltip = useCallback(
    ({ payload, label }) => {
      const formattedDate = label
        ? format(addYears(new Date(label), differenceYearsNumber), 'dd.MM.yyyy')
        : '-';
      const formattedEuroValue = formatNumbers(payload[2]?.value);

      return (
        <S.Tooltip>
          <Typography variant="body2">{`${formattedEuroValue} · ${formattedDate}`}</Typography>
        </S.Tooltip>
      );
    },
    [formatNumbers, differenceYearsNumber],
  );

  return (
    <S.Box>
      <Box height={475} width="100%" maxWidth="100%" position="relative">
        {!projectionData.length && (
          <S.ErrorLabel variant="body1">{t(messages.DWMPrognoseChartBoxErrorMsg)}</S.ErrorLabel>
        )}
        <ResponsiveContainer width="100%" height={450}>
          <AreaChart
            height={450}
            data={portfolioData}
            margin={{
              top: 15,
              left: 0,
            }}
          >
            <Legend
              wrapperStyle={{
                bottom: -24,
                left: 0,
                width: '100%',
              }}
              content={renderCustomizedLegend}
            />
            <Area
              strokeWidth={0}
              type="basis"
              fillOpacity={1}
              fill={`${theme.palette?.['black80'].main}22`}
              activeDot={false}
              dataKey="good"
            />
            <CartesianGrid vertical={false} height={150} />
            <Area
              strokeWidth={0}
              type="basis"
              fillOpacity={1}
              fill={theme.palette?.['blue10'].main}
              activeDot={false}
              dataKey="bad"
            />
            <Tooltip
              allowEscapeViewBox={{ x: true, y: true }}
              position={{ y: 40 }}
              wrapperStyle={{ border: 'none', outline: 'none', left: '-112px' }}
              content={CustomTooltip}
            />
            <Area
              type="basis"
              stroke={theme.palette?.['blue60'].main}
              fillOpacity={1}
              fill="url(#medianGradient)"
              dataKey="median"
              strokeWidth={3}
              // @ts-ignore
              activeDot={<CustomDot />}
            />
            <XAxis
              padding={{ right: 5, left: 5 }}
              dataKey="currentDate"
              axisLine={false}
              tick={false}
              tickLine={false}
            />
            <YAxis
              tickFormatter={(value) => formatTickValue(value, formatNumber)}
              orientation="left"
              tickCount={3}
              axisLine={false}
              tickLine={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </Box>
      <Box mt={4}>
        <S.RiskParagraph variant="body1">{prognoseChartRiskParagraph}</S.RiskParagraph>
      </Box>
    </S.Box>
  );
};

export default ChartBody;
